import { normalize } from "normalizr";
import * as React from "react";
import * as ReactDom from "react-dom";
import { Provider } from "react-redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { ISagaModule } from "redux-dynamic-modules-saga";
import { createCommonStore } from "~/common/createCommonStore";
import { MessageViewModel } from "~/EasyProjects/MessageBoard/MessageViewModel";
import { messageImagesSchema } from "~/normalizr/ImageAnnotationsSchema";
import { doUpdateEntities } from "~/Redux/Middleware/apiMiddleware";
import { renderReactComponent } from "~/sharedComponents/errorBoundary/renderReact";
import { FilesEntities } from "../types";
import {
    changeCurrentAttachmentId,
    closePopup,
    enterViewMode,
    openPopup,
    resetStore,
    setCurrentAttachmentIds,
    setInitialAnnotations,
    setMessageEditorInfo,
    showMessages,
} from "./actions";
import { DialogContainer } from "./containers/DialogContainer";
import { watchChangeCurrentImage } from "./sagas/changeCurrentImage";
import { messageBoardSync, messageBoardSyncProps } from "./sagas/messageBoardSync";
import { watchSaveAnnotation } from "./sagas/watchSaveAnnotation";
import { AnnotationItemExtended, Annotations } from "./types";

export interface ImageAnnotiationOptions extends messageBoardSyncProps {
    imageSrc: string;
    imageTitle: string;
    attachmentId: number;
    version: number;
    messageId: number;
}

const getSaveAnnotationModule = (messagesSyncModule?: GeneratorFunction): ISagaModule<any> => ({
    id: "annotation",
    sagas: [watchSaveAnnotation, watchChangeCurrentImage, messagesSyncModule],
});

export class ImageAnnotations {
    isInitialized: boolean = false;
    container: HTMLElement = document.createElement("div");
    store: ReturnType<typeof createCommonStore>;
    appComponent = (messagesSyncModule: any) => (
        <>
            <Provider store={this.store}>
                <DynamicModuleLoader modules={[getSaveAnnotationModule(messagesSyncModule)]}>
                    <DialogContainer close={this.destroy} />
                </DynamicModuleLoader>
            </Provider>
        </>
    );

    constructor(options: ImageAnnotiationOptions) {
        this.store = createCommonStore();
        this.isInitialized = true;
        this.destroy = this.destroy.bind(this);
        const { messageBoardVm, dataManager, messageId, attachmentId } = options;

        const messagesSyncModule = messageBoardSync({ messageBoardVm, dataManager });

        this.render(messagesSyncModule);
        const projectOrTask = messageBoardVm.ownerInfo.getInfo();
        this.store.dispatch(
            setMessageEditorInfo({
                projectId: messageBoardVm.ownerInfo.projectId,
                projectOrTask,
            })
        );

        const messages = options.messageBoardVm.messages();
        const files = messages.flatMap(x =>
            x
                .files()
                ?.filter(x => x?.isImage)
                .map(({ filename, source, version, ...props }) => ({
                    attachmentId: props._file.AttachmentID,
                    filename,
                    source,
                    version,
                    isShowVersion: props.isShowVersion(),
                    file: props._file,
                }))
        );

        const images = normalize(files, messageImagesSchema);

        if (attachmentId) {
            this.store.dispatch(doUpdateEntities(images.entities));
            this.store.dispatch(setCurrentAttachmentIds(images.result));
            this.store.dispatch(changeCurrentAttachmentId(attachmentId));
            const annotations = getAnnotationsFromMessageBoard(options);
            const entities = groupAnnotationsByAttachmentId(annotations);
            this.store.dispatch(setInitialAnnotations(entities));

            const annotation = getActiveAnnotation(messageId, annotations);

            if (entities?.[attachmentId]?.length > 0) {
                this.store.dispatch(showMessages());
            }

            if (annotation) {
                this.store.dispatch(enterViewMode(annotation));
            }

            this.store.dispatch(openPopup);
        }
    }

    render(messagesSyncModule: F): any {
        document.body.appendChild(this.container);
        renderReactComponent(this.appComponent(messagesSyncModule), this.container);
    }

    destroy() {
        if (this.isInitialized) {
            this.store.dispatch(closePopup);
            ReactDom.unmountComponentAtNode(this.container);
            document.body.removeChild(this.container);
            this.store.dispatch(resetStore);
            this.isInitialized = false;
        }
    }
}

export const getAnnotationsFromMessageBoard = (options: ImageAnnotiationOptions): AnnotationItemExtended[] => {
    const messages = options.messageBoardVm.messages();
    const messagesForThisAttachment: MessageViewModel[] = messages.filter(message => message?.annotation?.annotationId);

    return messagesForThisAttachment.map(message => ({
        message: {
            date: message.date,
            messageId: message.messageId,
            userId: message.user.id,
            userName: message.user.name,
            canEdit: message.canEdit(),
            canDelete: message.canDelete(),
        },
        data: {
            id: message.annotation.annotationId,
            text: message.text(),
        },
        geometry: JSON.parse(message.annotation.metadata),
        annotationId: message.annotation.annotationId,
        attachmentId: message.annotation.attachmentId,
    }));
};

export const getActiveAnnotation = (
    messageId: number,
    annotations: AnnotationItemExtended[]
): AnnotationItemExtended => {
    return annotations.find((annotation: AnnotationItemExtended) => {
        return annotation.message.messageId === messageId;
    });
};

export const groupAnnotationsByAttachmentId = (
    annotations: AnnotationItemExtended[],
    files?: FilesEntities
): Annotations => {
    const entities: Annotations = {};

    for (const item of annotations) {
        if ((files && files[item.attachmentId]) || !files) {
            (entities[item.attachmentId] = entities[item.attachmentId] || []).push(item);
        }
    }

    return entities;
};
